import {
  formatPhoneNumber,
  formatNumberToDollarAmount,
  extractFeeByDescription,
  LoanCostsCalculator,
} from '@mortgage-pos/utils'
import { Rate, LoanPurpose } from '@mortgage-pos/types'
import { PhoneAppVerifyDetailsVariant } from '@mortgage-pos/ui/pages/PhoneAppVerifyDetails'

export const buildSummaryInfo = (
  answers,
  rate?: Rate,
  phoneAppVariant?: PhoneAppVerifyDetailsVariant
) => {
  const {
    firstName,
    lastName,
    email,
    phone,
    estimatedValue,
    cashOut,
    refiReason,
    escrowRollIn,
    remainingBalance,
    escrow: waiveEscrow,
    rollInFees: willRollInFees,
    loanPurpose,
    propertyPurchasePrice,
    propertyDownPayment,
    grossAnnualIncome,
  } = answers

  const isRefi = loanPurpose === LoanPurpose.Refinance
  const addressValue = buildAddressData(answers)

  const loanCalcFinancials = {
    cashOut: cashOut ?? 0,
    totalFees: rate?.loanEstimateSections?.['D']?.total,
    totalEscrow: rate?.loanEstimateSections?.['G']?.total,
    lenderCredit: extractFeeByDescription(
      rate?.loanEstimateSections?.['J']?.fees,
      'Lender Credit'
    ),
    originationFee: extractFeeByDescription(
      rate?.loanEstimateSections?.['A']?.fees,
      'Loan origination fee'
    ),
    taxAndPrepaids:
      rate?.loanEstimateSections?.['E']?.total +
      rate?.loanEstimateSections?.['F']?.total,
    remainingBalance: isRefi
      ? remainingBalance
      : propertyPurchasePrice - propertyDownPayment,
  }

  const loanCostsCalculator = new LoanCostsCalculator(loanCalcFinancials, {
    waiveEscrow: waiveEscrow === 'Yes',
    escrowRollIn: escrowRollIn === 'Yes',
    willRollInFees: willRollInFees === 'Yes',
  })

  const loanCosts = loanCostsCalculator.getLoanCosts()
  const totalFees = loanCosts.finalClosingCosts
  const cashoutMinusFees = cashOut - totalFees + loanCosts.financedCosts

  const isPrimaryPhoneAppVariant =
    phoneAppVariant === PhoneAppVerifyDetailsVariant.Primary
  const isCoBorrowerPhoneAppVariant =
    phoneAppVariant === PhoneAppVerifyDetailsVariant.CoBorrower

  if (isPrimaryPhoneAppVariant || isCoBorrowerPhoneAppVariant) {
    const incomes = isCoBorrowerPhoneAppVariant
      ? answers.coBorrowerIncomeSources
      : answers.incomeSources

    const otherIncomes = isCoBorrowerPhoneAppVariant
      ? answers.coBorrowerOtherIncomes
      : answers.otherIncomes

    const borrowerInfo = buildBorrowerInfo(answers, phoneAppVariant)
    const incomeInfo = buildIncomeInfo(incomes)
    const otherIncomeInfo = buildOtherIncomeInfo(otherIncomes)
    const propertyInfo = buildPropertyInfo(
      answers,
      isRefi,
      addressValue,
      isPrimaryPhoneAppVariant
    )

    return [...propertyInfo, borrowerInfo, ...incomeInfo, ...otherIncomeInfo]
  }
  return isRefi
    ? [
        {
          title: 'Property Info',
          properties: [
            {
              heading: 'Address',
              value: addressValue,
            },
            {
              heading: 'Property Value',
              value: formatNumberToDollarAmount(estimatedValue),
            },
          ],
        },
        {
          title: 'Personal Info',
          properties: [
            {
              heading: 'Name',
              value: firstName?.concat(' ', lastName),
            },
            { heading: 'Email', value: email },
            { heading: 'Phone', value: formatPhoneNumber(phone) },
            {
              heading: 'Income Type',
              value: IncomeTypeMap(answers.incomeSources?.[0]?.incomeType),
            },
            {
              heading: 'Annual Income',
              value: formatNumberToDollarAmount(
                answers.incomeSources?.[0]?.grossAnnualIncomeAmount
              ),
            },
          ],
        },
        {
          title: 'Refi options',
          properties: [
            { heading: 'Goal', value: RefiReasonMap(refiReason) },
            {
              heading: 'Taxes & Insurance',
              value: EscrowPaymentMap(waiveEscrow),
            },
            {
              heading: 'Roll In Fees',
              value: willRollInFees,
            },
            {
              heading: 'Cash-out',
              value: formatNumberToDollarAmount(cashOut || undefined),
            },
            {
              heading: 'Total Fees',
              value: formatNumberToDollarAmount(
                loanCosts.finalClosingCosts || undefined
              ),
            },
            {
              heading: 'Cash out minus fees',
              value: formatNumberToDollarAmount(cashoutMinusFees || undefined),
            },
            {
              heading: 'Mortgage Balance',
              value: formatNumberToDollarAmount(remainingBalance),
            },
            {
              heading: 'Total Loan Amount',
              value: formatNumberToDollarAmount(
                loanCosts.lockedLoanAmount || undefined
              ),
            },
          ],
        },
      ]
    : [
        {
          title: 'Property Info',
          properties: [
            {
              heading: 'Address',
              value: addressValue,
            },
          ],
        },
        {
          title: 'Personal Info',
          properties: [
            {
              heading: 'Name',
              value: firstName?.concat(' ', lastName),
            },
            { heading: 'Email', value: email },
            { heading: 'Phone', value: formatPhoneNumber(phone) },
            {
              heading: 'Annual Income',
              value: formatNumberToDollarAmount(grossAnnualIncome),
            },
          ],
        },
        {
          title: 'Purchase options',
          properties: [
            {
              heading: 'Purchase Price',
              value: formatNumberToDollarAmount(propertyPurchasePrice),
            },

            {
              heading: 'Down Payment',
              value: formatNumberToDollarAmount(propertyDownPayment),
            },
            {
              heading: 'Loan Amount',
              value: formatNumberToDollarAmount(
                loanCosts.lockedLoanAmount || undefined
              ),
            },
          ],
        },
      ]
}

const IncomeTypeMap = (incomeType) => {
  switch (incomeType) {
    case 'w2':
      return 'Employment (W-2)'
    case 'selfEmployed':
      return 'Self Employed'
    case 'retired':
      return 'Retired'
    case 'noIncome':
      return 'No Income'
    default:
      return ''
  }
}

const RefiReasonMap = (refiReason) => {
  switch (refiReason) {
    case 'cashOut':
      return 'Access my Equity'
    case 'lowerPayment':
      return 'Lower Monthly Payment'
    case 'fasterPayoff':
      return 'Faster Payoff'
    case 'lowerInterestRate':
      return 'Lower Interest Rate'
    case 'notSure':
      return 'Not Sure'
    default:
      return ''
  }
}

const EscrowPaymentMap = (waiveEscrow) => {
  switch (waiveEscrow) {
    case 'No':
      return 'Pay with escrow account'
    case 'Yes':
      return 'Pay directly to my provider'
    default:
      return ''
  }
}

const buildBorrowerInfo = (answers, phoneAppVariant) => {
  const name =
    phoneAppVariant === PhoneAppVerifyDetailsVariant.CoBorrower
      ? answers.coBorrowerPersonalInfo?.[0]?.firstName?.concat(
          ' ',
          answers.coBorrowerPersonalInfo?.[0]?.lastName
        )
      : answers.firstName?.concat(' ', answers.lastName)

  const email =
    phoneAppVariant === PhoneAppVerifyDetailsVariant.CoBorrower
      ? answers.coBorrowerPersonalInfo?.[0]?.email
      : answers.email

  return {
    title: 'Borrower Info',
    properties: [
      {
        heading: 'Name',
        value: name,
      },
      { heading: 'Email', value: email },
    ],
  }
}

const buildIncomeInfo = (incomeSources) => {
  const incomeData = []

  incomeSources?.forEach((incomeSource) =>
    incomeData.unshift({
      title: `Employment Info${
        incomeSource.employerName ? ' - ' + incomeSource.employerName : ''
      }`,
      properties: [
        {
          heading: 'Type',
          value: IncomeTypeMap(incomeSource.incomeType),
        },
        {
          heading: 'Annual Income',
          value: formatNumberToDollarAmount(
            incomeSource.grossAnnualIncomeAmount,
            0
          ),
        },
        {
          heading: 'Overtime',
          value: formatNumberToDollarAmount(incomeSource.overtime, 0),
        },
        {
          heading: 'Bonus',
          value: formatNumberToDollarAmount(incomeSource.bonus, 0),
        },
        {
          heading: 'Commission',
          value: formatNumberToDollarAmount(incomeSource.commissions, 0),
        },
      ],
    })
  )
  return incomeData
}

const buildOtherIncomeInfo = (otherIncomes) => {
  const otherIncomeData = []

  otherIncomes?.forEach((income) =>
    otherIncomeData.unshift({
      title: `Other Income Info - ${income.otherIncomeSource}`,
      properties: [
        {
          heading: 'Annual Amount',
          value: formatNumberToDollarAmount(income.otherAnnualIncomeAmount, 0),
        },
      ],
    })
  )

  return otherIncomeData
}

const buildAddressData = (answers) => {
  if (answers.autoCompleteAddressFields)
    return answers.autoCompleteAddressFields

  const { street, city, state, zipCode } = answers

  if (street && city && state) {
    return `${street}\n${city}, ${state} ${zipCode}`
  }

  return 'TBD'
}

const buildPropertyInfo = (
  answers,
  isRefi,
  addressValue,
  isPrimaryPhoneAppVariant
) => {
  const { estimatedValue, propertyDownPayment } = answers

  return isPrimaryPhoneAppVariant
    ? [
        {
          title: 'Property Info',
          properties: [
            {
              heading: 'Address',
              value: addressValue,
            },
            isRefi
              ? {
                  heading: 'Property Value',
                  value: formatNumberToDollarAmount(estimatedValue),
                }
              : {
                  heading: 'Down Payment',
                  value: formatNumberToDollarAmount(propertyDownPayment),
                },
          ],
        },
      ]
    : []
}
